import React, { useState, useRef, useEffect, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import { Link2, FileText, ArrowLeft, Upload, X, Image, Search } from 'lucide-react';
import api from '../services/api';
import { FUEL_TYPES, TRANSMISSION_OPTIONS, BODY_TYPES, COLORS, INTERIOR_TYPES, PAINT_TYPES, DRIVE_TYPES, EURO_STANDARDS } from '../constants/vehicleFields';
import { searchMakes, getModelsForMake } from '../hooks/useReferenceData';

const VehicleAdd = () => {
  const [activeTab, setActiveTab] = useState('manual');
  const [importUrl, setImportUrl] = useState('');
  const [manualForm, setManualForm] = useState({
    make: '',
    model: '',
    version: '',
    year: new Date().getFullYear(),
    mileage: '',
    price: '',
    fuel_type: '',
    transmission: '',
    power_kw: '',
    color: '',
    doors: '',
    body_type: '',
    description: '',
    // Extended specs
    seats: '',
    power_hp: '',
    engine_displacement_cc: '',
    gears: '',
    drive_type: '',
    interior_type: '',
    interior_color: '',
    fuel_consumption: '',
    co2_emissions: '',
    euro_standard: '',
    previous_owners: '',
    first_registration: '',
    // Condition & history
    warranty: '',
    had_accident: '',
    has_full_service_history: '',
    non_smoking: '',
    has_particle_filter: '',
    tax_deductible: '',
    weight: '',
    cylinders: '',
    paint_type: '',
    purchase_price: '',
    reconditioning_cost: '',
  });
  const [selectedFiles, setSelectedFiles] = useState([]);
  const [previewUrls, setPreviewUrls] = useState([]);
  const [loading, setLoading] = useState(false);
  const [uploadProgress, setUploadProgress] = useState('');
  const [error, setError] = useState('');
  const [vinInput, setVinInput] = useState('');
  const [vinMileage, setVinMileage] = useState('');
  const [vinLoading, setVinLoading] = useState(false);
  const [vinResult, setVinResult] = useState(null);
  const [vinAutoconnectData, setVinAutoconnectData] = useState(null);
  const [makeSuggestions, setMakeSuggestions] = useState([]);
  const [modelSuggestions, setModelSuggestions] = useState([]);
  const [showMakeSuggestions, setShowMakeSuggestions] = useState(false);
  const [showModelSuggestions, setShowModelSuggestions] = useState(false);
  const [selectedMakeKey, setSelectedMakeKey] = useState(null);
  const [customColor, setCustomColor] = useState(false);
  const makeRef = useRef(null);
  const modelRef = useRef(null);
  const navigate = useNavigate();
  const fileInputRef = useRef(null);

  // Debounced make search
  const makeSearchTimeout = useRef(null);
  const handleMakeSearch = useCallback((value) => {
    if (makeSearchTimeout.current) clearTimeout(makeSearchTimeout.current);
    if (!value || value.length < 1) {
      setMakeSuggestions([]);
      setShowMakeSuggestions(false);
      return;
    }
    makeSearchTimeout.current = setTimeout(async () => {
      const results = await searchMakes(value);
      setMakeSuggestions(results);
      setShowMakeSuggestions(results.length > 0);
    }, 200);
  }, []);

  const handleMakeSelect = useCallback(async (make) => {
    setManualForm(prev => ({ ...prev, make: make.name, model: '' }));
    setSelectedMakeKey(make.key);
    setShowMakeSuggestions(false);
    const models = await getModelsForMake(make.key);
    setModelSuggestions(models);
  }, []);

  const handleModelSelect = useCallback((model) => {
    setManualForm(prev => ({ ...prev, model: model.name }));
    setShowModelSuggestions(false);
  }, []);

  // Close suggestions on click outside
  useEffect(() => {
    const handleClickOutside = (e) => {
      if (makeRef.current && !makeRef.current.contains(e.target)) setShowMakeSuggestions(false);
      if (modelRef.current && !modelRef.current.contains(e.target)) setShowModelSuggestions(false);
    };
    document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, []);

  const handleImport = async (e) => {
    e.preventDefault();
    if (!importUrl) return;

    setError('');
    setLoading(true);

    try {
      const response = await api.post('/vehicles/import', { url: importUrl });
      navigate(`/vehicles/${response.data.id}`);
    } catch (err) {
      setError(err.response?.data?.detail || 'Erreur lors de l\'importation');
    } finally {
      setLoading(false);
    }
  };

  const handleManualSubmit = async (e) => {
    e.preventDefault();
    setError('');
    setLoading(true);

    try {
      const data = {
        ...manualForm,
        year: parseInt(manualForm.year),
        mileage: manualForm.mileage ? parseInt(manualForm.mileage) : null,
        price: parseInt(manualForm.price),
        power_kw: manualForm.power_kw ? parseInt(manualForm.power_kw) : null,
        doors: manualForm.doors ? parseInt(manualForm.doors) : null,
        color: manualForm.color || null,
        body_type: manualForm.body_type || null,
        // Extended specs
        seats: manualForm.seats ? parseInt(manualForm.seats) : null,
        power_hp: manualForm.power_hp ? parseInt(manualForm.power_hp) : null,
        engine_displacement_cc: manualForm.engine_displacement_cc ? parseInt(manualForm.engine_displacement_cc) : null,
        gears: manualForm.gears ? parseInt(manualForm.gears) : null,
        drive_type: manualForm.drive_type || null,
        interior_type: manualForm.interior_type || null,
        interior_color: manualForm.interior_color || null,
        fuel_consumption: manualForm.fuel_consumption ? parseFloat(manualForm.fuel_consumption) : null,
        co2_emissions: manualForm.co2_emissions ? parseInt(manualForm.co2_emissions) : null,
        euro_standard: manualForm.euro_standard || null,
        previous_owners: manualForm.previous_owners ? parseInt(manualForm.previous_owners) : null,
        first_registration: manualForm.first_registration || null,
        warranty: manualForm.warranty || null,
        had_accident: manualForm.had_accident === '' ? null : manualForm.had_accident === 'true',
        has_full_service_history: manualForm.has_full_service_history === '' ? null : manualForm.has_full_service_history === 'true',
        non_smoking: manualForm.non_smoking === '' ? null : manualForm.non_smoking === 'true',
        has_particle_filter: manualForm.has_particle_filter === '' ? null : manualForm.has_particle_filter === 'true',
        tax_deductible: manualForm.tax_deductible === '' ? null : manualForm.tax_deductible === 'true',
        weight: manualForm.weight || null,
        cylinders: manualForm.cylinders ? parseInt(manualForm.cylinders) : null,
        paint_type: manualForm.paint_type || null,
        purchase_price: manualForm.purchase_price ? parseInt(manualForm.purchase_price) : undefined,
        reconditioning_cost: manualForm.reconditioning_cost ? parseFloat(manualForm.reconditioning_cost) : undefined,
        images: []
      };

      const response = await api.post('/vehicles', data);
      const vehicleId = response.data.id;

      // Upload photos if any selected
      if (selectedFiles.length > 0) {
        setUploadProgress(`Upload des photos (0/${selectedFiles.length})...`);
        const formData = new FormData();
        for (let i = 0; i < selectedFiles.length; i++) {
          formData.append('files', selectedFiles[i]);
        }
        setUploadProgress(`Upload des photos (${selectedFiles.length}/${selectedFiles.length})...`);
        await api.post(`/vehicles/${vehicleId}/images`, formData, {
          headers: { 'Content-Type': 'multipart/form-data' }
        });
      }

      navigate(`/vehicles/${vehicleId}`);
    } catch (err) {
      setError(err.response?.data?.detail || 'Erreur lors de la création');
    } finally {
      setLoading(false);
      setUploadProgress('');
    }
  };

  const handleManualChange = (e) => {
    const { name, value } = e.target;
    setManualForm(prev => ({ ...prev, [name]: value }));
  };

  const handleVinLookup = async (e) => {
    e.preventDefault();
    if (!vinInput || vinInput.length !== 17) return;
    setError('');
    setVinLoading(true);
    setVinResult(null);
    setVinAutoconnectData(null);
    try {
      const response = await api.post('/vehicles/vin-lookup', {
        vin: vinInput.toUpperCase(),
        mileage: vinMileage ? parseInt(vinMileage) : null,
      });
      const { vehicle_fields, autoconnect_data } = response.data;
      setManualForm(prev => ({
        ...prev,
        ...vehicle_fields,
        mileage: vinMileage || vehicle_fields.mileage || '',
      }));
      setVinResult(vehicle_fields);
      setVinAutoconnectData(autoconnect_data);
    } catch (err) {
      setError(err.response?.data?.detail || 'Erreur lors de la recherche VIN');
    } finally {
      setVinLoading(false);
    }
  };

  const handleVinSubmit = async (e) => {
    e.preventDefault();
    setError('');
    setLoading(true);
    try {
      const data = {
        ...manualForm,
        vin: vinInput.toUpperCase(),
        year: parseInt(manualForm.year),
        mileage: manualForm.mileage ? parseInt(manualForm.mileage) : null,
        price: parseInt(manualForm.price),
        power_kw: manualForm.power_kw ? parseInt(manualForm.power_kw) : null,
        doors: manualForm.doors ? parseInt(manualForm.doors) : null,
        color: manualForm.color || null,
        body_type: manualForm.body_type || null,
        seats: manualForm.seats ? parseInt(manualForm.seats) : null,
        power_hp: manualForm.power_hp ? parseInt(manualForm.power_hp) : null,
        engine_displacement_cc: manualForm.engine_displacement_cc ? parseInt(manualForm.engine_displacement_cc) : null,
        gears: manualForm.gears ? parseInt(manualForm.gears) : null,
        drive_type: manualForm.drive_type || null,
        interior_type: manualForm.interior_type || null,
        interior_color: manualForm.interior_color || null,
        fuel_consumption: manualForm.fuel_consumption ? parseFloat(manualForm.fuel_consumption) : null,
        co2_emissions: manualForm.co2_emissions ? parseInt(manualForm.co2_emissions) : null,
        euro_standard: manualForm.euro_standard || null,
        previous_owners: manualForm.previous_owners ? parseInt(manualForm.previous_owners) : null,
        first_registration: manualForm.first_registration || null,
        warranty: manualForm.warranty || null,
        had_accident: manualForm.had_accident === '' ? null : manualForm.had_accident === 'true',
        has_full_service_history: manualForm.has_full_service_history === '' ? null : manualForm.has_full_service_history === 'true',
        non_smoking: manualForm.non_smoking === '' ? null : manualForm.non_smoking === 'true',
        has_particle_filter: manualForm.has_particle_filter === '' ? null : manualForm.has_particle_filter === 'true',
        tax_deductible: manualForm.tax_deductible === '' ? null : manualForm.tax_deductible === 'true',
        weight: manualForm.weight || null,
        cylinders: manualForm.cylinders ? parseInt(manualForm.cylinders) : null,
        paint_type: manualForm.paint_type || null,
        purchase_price: manualForm.purchase_price ? parseInt(manualForm.purchase_price) : undefined,
        reconditioning_cost: manualForm.reconditioning_cost ? parseFloat(manualForm.reconditioning_cost) : undefined,
        autoconnect_data: vinAutoconnectData,
        images: [],
      };
      const response = await api.post('/vehicles', data);
      const vehicleId = response.data.id;
      if (selectedFiles.length > 0) {
        setUploadProgress(`Upload des photos (0/${selectedFiles.length})...`);
        const formData = new FormData();
        selectedFiles.forEach(file => formData.append('files', file));
        setUploadProgress(`Upload des photos (${selectedFiles.length}/${selectedFiles.length})...`);
        await api.post(`/vehicles/${vehicleId}/images`, formData, {
          headers: { 'Content-Type': 'multipart/form-data' },
        });
      }
      navigate(`/vehicles/${vehicleId}`);
    } catch (err) {
      setError(err.response?.data?.detail || 'Erreur lors de la création');
    } finally {
      setLoading(false);
      setUploadProgress('');
    }
  };

  const handleFileSelect = (e) => {
    const files = Array.from(e.target.files || []);
    if (files.length === 0) return;

    setSelectedFiles(prev => [...prev, ...files]);

    // Generate preview URLs
    const newPreviews = files.map(file => URL.createObjectURL(file));
    setPreviewUrls(prev => [...prev, ...newPreviews]);
    e.target.value = '';
  };

  const removeFile = (index) => {
    URL.revokeObjectURL(previewUrls[index]);
    setSelectedFiles(prev => prev.filter((_, i) => i !== index));
    setPreviewUrls(prev => prev.filter((_, i) => i !== index));
  };

  const inputClass = "w-full px-3 py-2 bg-dark-input border border-dark-border rounded-md text-primary placeholder-muted focus:outline-none focus:ring-carmin focus:border-carmin text-sm";
  const labelClass = "block text-sm font-medium text-label mb-1";

  return (
    <div className="max-w-3xl mx-auto">
      <button
        onClick={() => navigate(-1)}
        className="inline-flex items-center text-secondary hover:text-primary mb-6"
      >
        <ArrowLeft className="h-4 w-4 mr-2" />
        Retour
      </button>

      <h1 className="text-2xl font-bold text-primary mb-6">Ajouter un véhicule</h1>

      {/* Tabs */}
      <div className="border-b border-dark-border mb-6">
        <nav className="flex space-x-8">
          <button
            onClick={() => setActiveTab('manual')}
            className={`py-4 px-1 border-b-2 font-medium text-sm ${
              activeTab === 'manual'
                ? 'border-carmin text-carmin'
                : 'border-transparent text-muted hover:text-label'
            }`}
          >
            <FileText className="h-4 w-4 inline mr-2" />
            Création manuelle
          </button>
          <button
            onClick={() => setActiveTab('import')}
            className={`py-4 px-1 border-b-2 font-medium text-sm ${
              activeTab === 'import'
                ? 'border-carmin text-carmin'
                : 'border-transparent text-muted hover:text-label'
            }`}
          >
            <Link2 className="h-4 w-4 inline mr-2" />
            Import AutoScout24
          </button>
          <button
            onClick={() => setActiveTab('vin')}
            className={`py-4 px-1 border-b-2 font-medium text-sm ${
              activeTab === 'vin'
                ? 'border-carmin text-carmin'
                : 'border-transparent text-muted hover:text-label'
            }`}
          >
            <Search className="h-4 w-4 inline mr-2" />
            Recherche VIN
          </button>
        </nav>
      </div>

      {error && (
        <div className="bg-red-500/10 text-red-400 p-4 rounded-md mb-6">
          {error}
        </div>
      )}

      {/* Import tab */}
      {activeTab === 'import' && (
        <div className="bg-dark-card rounded-lg p-6">
          <p className="text-secondary mb-4">
            Collez l'URL d'une annonce AutoScout24 pour importer automatiquement les données du véhicule.
          </p>
          <form onSubmit={handleImport}>
            <div className="mb-4">
              <label htmlFor="url" className={labelClass}>
                URL AutoScout24
              </label>
              <input
                type="url"
                id="url"
                value={importUrl}
                onChange={(e) => setImportUrl(e.target.value)}
                placeholder="https://www.autoscout24.be/fr/offres/..."
                className={inputClass}
                required
              />
            </div>
            <button
              type="submit"
              disabled={loading}
              className="w-full py-2.5 px-4 bg-carmin text-white rounded-md hover:bg-carmin-light disabled:opacity-50 font-medium"
            >
              {loading ? 'Importation...' : 'Importer'}
            </button>
          </form>
        </div>
      )}

      {/* VIN Lookup tab */}
      {activeTab === 'vin' && (
        <div className="space-y-6">
          <div className="bg-dark-card rounded-lg p-6">
            <p className="text-secondary mb-4">
              Entrez le numéro de châssis (VIN) pour pré-remplir automatiquement les données du véhicule.
            </p>
            <form onSubmit={handleVinLookup} className="space-y-4">
              <div className="grid grid-cols-2 gap-4">
                <div>
                  <label className={labelClass}>Numéro VIN *</label>
                  <input
                    type="text"
                    value={vinInput}
                    onChange={(e) => setVinInput(e.target.value.toUpperCase())}
                    placeholder="WAUZZZ4G5HN009507"
                    maxLength={17}
                    className={inputClass}
                    required
                  />
                  <p className="mt-1 text-xs text-muted">17 caractères — visible sur le certificat d'immatriculation</p>
                </div>
                <div>
                  <label className={labelClass}>Kilométrage actuel</label>
                  <input
                    type="number"
                    value={vinMileage}
                    onChange={(e) => setVinMileage(e.target.value)}
                    placeholder="85000"
                    min="0"
                    className={inputClass}
                  />
                  <p className="mt-1 text-xs text-muted">Optionnel — pour la valeur résiduelle</p>
                </div>
              </div>
              <button
                type="submit"
                disabled={vinLoading || vinInput.length !== 17}
                className="w-full py-2.5 px-4 bg-carmin text-white rounded-md hover:bg-carmin-light disabled:opacity-50 font-medium"
              >
                {vinLoading ? 'Recherche en cours...' : 'Rechercher'}
              </button>
            </form>
          </div>

          {vinAutoconnectData && (
            <div className="bg-dark-card rounded-lg p-6">
              <h2 className="text-lg font-semibold text-primary mb-4">Données Autoconnect</h2>
              {vinAutoconnectData.legal_status_code === 'STL' && (
                <div className="bg-red-500/10 text-red-400 p-3 rounded-md mb-4 text-sm font-medium">
                  Attention : ce véhicule est signalé comme volé
                </div>
              )}
              {vinAutoconnectData.status_code && vinAutoconnectData.status_code !== 'NOR' && vinAutoconnectData.legal_status_code !== 'STL' && (
                <div className="bg-yellow-500/10 text-yellow-400 p-3 rounded-md mb-4 text-sm">
                  Statut véhicule : {vinAutoconnectData.status_code}
                </div>
              )}
              <div className="grid grid-cols-2 md:grid-cols-3 gap-4 text-sm">
                {vinAutoconnectData.catalog_price_excl_vat && (
                  <div><span className="text-muted">Prix catalogue HTVA</span><p className="text-primary font-medium">{Number(vinAutoconnectData.catalog_price_excl_vat).toLocaleString('fr-BE')} €</p></div>
                )}
                {vinAutoconnectData.version_name && (
                  <div><span className="text-muted">Version commerciale</span><p className="text-primary font-medium">{vinAutoconnectData.version_name}</p></div>
                )}
                {vinAutoconnectData.b2b_value && (
                  <div><span className="text-muted">Valeur B2B</span><p className="text-primary font-medium">{Number(vinAutoconnectData.b2b_value).toLocaleString('fr-BE')} €</p></div>
                )}
                {vinAutoconnectData.b2c_value && (
                  <div><span className="text-muted">Valeur B2C</span><p className="text-primary font-medium">{Number(vinAutoconnectData.b2c_value).toLocaleString('fr-BE')} €</p></div>
                )}
                {vinAutoconnectData.c2c_value && (
                  <div><span className="text-muted">Valeur C2C</span><p className="text-primary font-medium">{Number(vinAutoconnectData.c2c_value).toLocaleString('fr-BE')} €</p></div>
                )}
                {vinAutoconnectData.fiscal_hp && (
                  <div><span className="text-muted">CV fiscaux</span><p className="text-primary font-medium">{vinAutoconnectData.fiscal_hp}</p></div>
                )}
                {vinAutoconnectData.eco_score && (
                  <div><span className="text-muted">Ecoscore</span><p className="text-primary font-medium">{vinAutoconnectData.eco_score}</p></div>
                )}
                {vinAutoconnectData.road_tax_flanders && (
                  <div><span className="text-muted">Taxe circulation (Flandre)</span><p className="text-primary font-medium">{Number(vinAutoconnectData.road_tax_flanders).toLocaleString('fr-BE')} €/an</p></div>
                )}
                {vinAutoconnectData.road_tax_brussels && (
                  <div><span className="text-muted">Taxe circulation (Bruxelles)</span><p className="text-primary font-medium">{Number(vinAutoconnectData.road_tax_brussels).toLocaleString('fr-BE')} €/an</p></div>
                )}
                {vinAutoconnectData.road_tax_walloon && (
                  <div><span className="text-muted">Taxe circulation (Wallonie)</span><p className="text-primary font-medium">{Number(vinAutoconnectData.road_tax_walloon).toLocaleString('fr-BE')} €/an</p></div>
                )}
                {vinAutoconnectData.registration_tax_flanders && (
                  <div><span className="text-muted">Taxe immat. (Flandre)</span><p className="text-primary font-medium">{Number(vinAutoconnectData.registration_tax_flanders).toLocaleString('fr-BE')} €</p></div>
                )}
                {vinAutoconnectData.registration_tax_brussels && (
                  <div><span className="text-muted">Taxe immat. (Bruxelles)</span><p className="text-primary font-medium">{Number(vinAutoconnectData.registration_tax_brussels).toLocaleString('fr-BE')} €</p></div>
                )}
                {vinAutoconnectData.registration_tax_walloon && (
                  <div><span className="text-muted">Taxe immat. (Wallonie)</span><p className="text-primary font-medium">{Number(vinAutoconnectData.registration_tax_walloon).toLocaleString('fr-BE')} €</p></div>
                )}
                {vinAutoconnectData.benefit_in_kind && (
                  <div><span className="text-muted">Avantage en nature</span><p className="text-primary font-medium">{Number(vinAutoconnectData.benefit_in_kind).toLocaleString('fr-BE')} €/an</p></div>
                )}
                {vinAutoconnectData.first_registration_date && (
                  <div><span className="text-muted">1ère immatriculation</span><p className="text-primary font-medium">{vinAutoconnectData.first_registration_date}</p></div>
                )}
                {vinAutoconnectData.last_registration_date && (
                  <div><span className="text-muted">Dernière immatriculation</span><p className="text-primary font-medium">{vinAutoconnectData.last_registration_date}</p></div>
                )}
                {vinAutoconnectData.technical_control_valid_date && (
                  <div><span className="text-muted">CT valide jusqu'au</span><p className="text-primary font-medium">{vinAutoconnectData.technical_control_valid_date}</p></div>
                )}
              </div>
              {(vinAutoconnectData.future_b2b_value || vinAutoconnectData.future_b2c_value || vinAutoconnectData.future_c2c_value) && (
                <div className="mt-4 pt-4 border-t border-dark-border">
                  <h3 className="text-sm font-semibold text-muted mb-3">Valeurs résiduelles futures{vinAutoconnectData.future_date ? ` (${vinAutoconnectData.future_date})` : ''}</h3>
                  <div className="grid grid-cols-3 gap-4 text-sm">
                    {vinAutoconnectData.future_b2b_value && (
                      <div><span className="text-muted">B2B futur</span><p className="text-primary font-medium">{Number(vinAutoconnectData.future_b2b_value).toLocaleString('fr-BE')} €</p></div>
                    )}
                    {vinAutoconnectData.future_b2c_value && (
                      <div><span className="text-muted">B2C futur</span><p className="text-primary font-medium">{Number(vinAutoconnectData.future_b2c_value).toLocaleString('fr-BE')} €</p></div>
                    )}
                    {vinAutoconnectData.future_c2c_value && (
                      <div><span className="text-muted">C2C futur</span><p className="text-primary font-medium">{Number(vinAutoconnectData.future_c2c_value).toLocaleString('fr-BE')} €</p></div>
                    )}
                  </div>
                </div>
              )}
            </div>
          )}

          {vinResult && (
            <form onSubmit={handleVinSubmit} className="space-y-6">
              <div className="bg-dark-card rounded-lg p-6">
                <h2 className="text-lg font-semibold text-primary mb-4 flex items-center">
                  <Image className="h-5 w-5 mr-2 text-secondary" />
                  Photos
                </h2>
                {previewUrls.length > 0 && (
                  <div className="grid grid-cols-3 sm:grid-cols-4 gap-3 mb-4">
                    {previewUrls.map((url, index) => (
                      <div key={index} className="relative aspect-[4/3] rounded-lg overflow-hidden bg-dark-hover">
                        <img src={url} alt="" className="w-full h-full object-cover" />
                        <button type="button" onClick={() => removeFile(index)} className="absolute top-1 right-1 bg-red-500 text-white rounded-full p-0.5 hover:bg-red-600">
                          <X className="h-3.5 w-3.5" />
                        </button>
                      </div>
                    ))}
                  </div>
                )}
                <input type="file" ref={fileInputRef} onChange={handleFileSelect} multiple accept="image/*" className="hidden" />
                <button type="button" onClick={() => fileInputRef.current?.click()} className="w-full py-8 border-2 border-dashed border-dark-border rounded-lg text-muted hover:border-carmin hover:text-carmin transition-colors flex flex-col items-center">
                  <Upload className="h-8 w-8 mb-2" />
                  <span className="text-sm font-medium">Cliquez pour ajouter des photos</span>
                </button>
              </div>

              <div className="bg-dark-card rounded-lg p-6">
                <h2 className="text-lg font-semibold text-primary mb-4">Informations du véhicule</h2>
                <p className="text-xs text-muted mb-4">Pré-rempli depuis le VIN — vous pouvez modifier les valeurs</p>
                <div className="space-y-4">
                  <div className="grid grid-cols-2 gap-4">
                    <div><label className={labelClass}>Marque *</label><input type="text" name="make" value={manualForm.make} onChange={handleManualChange} required className={inputClass} /></div>
                    <div><label className={labelClass}>Modèle *</label><input type="text" name="model" value={manualForm.model} onChange={handleManualChange} required className={inputClass} /></div>
                  </div>
                  <div><label className={labelClass}>Version / Finition</label><input type="text" name="version" value={manualForm.version} onChange={handleManualChange} className={inputClass} /></div>
                  <div className="grid grid-cols-2 gap-4">
                    <div><label className={labelClass}>Année *</label><input type="number" name="year" value={manualForm.year} onChange={handleManualChange} required className={inputClass} /></div>
                    <div><label className={labelClass}>Prix (€) *</label><input type="number" name="price" value={manualForm.price} onChange={handleManualChange} min="0" required className={inputClass} /></div>
                  </div>
                  <div className="grid grid-cols-2 gap-4">
                    <div><label className={labelClass}>Prix d'achat (€)</label><input type="number" name="purchase_price" value={manualForm.purchase_price} onChange={handleManualChange} min="0" className={inputClass} placeholder="Optionnel" /></div>
                    <div><label className={labelClass}>Reconditionnement (€)</label><input type="number" name="reconditioning_cost" value={manualForm.reconditioning_cost} onChange={handleManualChange} min="0" className={inputClass} placeholder="Optionnel" /></div>
                  </div>
                  <div className="grid grid-cols-2 gap-4">
                    <div><label className={labelClass}>Kilométrage</label><input type="number" name="mileage" value={manualForm.mileage} onChange={handleManualChange} min="0" className={inputClass} /></div>
                    <div><label className={labelClass}>Puissance (kW)</label><input type="number" name="power_kw" value={manualForm.power_kw} onChange={handleManualChange} className={inputClass} /></div>
                  </div>
                  <div className="grid grid-cols-2 gap-4">
                    <div>
                      <label className={labelClass}>Carburant</label>
                      <select name="fuel_type" value={manualForm.fuel_type} onChange={handleManualChange} className={inputClass}>
                        <option value="">Sélectionner</option>
                        {FUEL_TYPES.map(ft => <option key={ft.value} value={ft.value}>{ft.label}</option>)}
                      </select>
                    </div>
                    <div>
                      <label className={labelClass}>Transmission</label>
                      <select name="transmission" value={manualForm.transmission} onChange={handleManualChange} className={inputClass}>
                        <option value="">Sélectionner</option>
                        {TRANSMISSION_OPTIONS.map(t => <option key={t.value} value={t.value}>{t.label}</option>)}
                      </select>
                    </div>
                  </div>
                  <div className="grid grid-cols-3 gap-4">
                    <div>
                      <label className={labelClass}>Couleur</label>
                      {customColor ? (
                        <div className="flex gap-1">
                          <input type="text" name="color" value={manualForm.color} onChange={handleManualChange} placeholder="Ex: Vert olive" autoFocus className={inputClass} />
                          <button type="button" onClick={() => { setCustomColor(false); setManualForm(prev => ({ ...prev, color: '' })); }} className="px-2 text-muted hover:text-primary shrink-0" title="Retour à la liste">
                            <X className="h-4 w-4" />
                          </button>
                        </div>
                      ) : (
                        <select name="color" value={manualForm.color} onChange={(e) => { if (e.target.value === '__custom__') { setCustomColor(true); setManualForm(prev => ({ ...prev, color: '' })); } else { handleManualChange(e); } }} className={inputClass}>
                          <option value="">Sélectionner</option>
                          {COLORS.map(c => <option key={c.value} value={c.value}>{c.label}</option>)}
                          <option value="__custom__">Autre...</option>
                        </select>
                      )}
                    </div>
                    <div>
                      <label className={labelClass}>Portes</label>
                      <select name="doors" value={manualForm.doors} onChange={handleManualChange} className={inputClass}>
                        <option value="">-</option>
                        <option value="2">2</option><option value="3">3</option><option value="4">4</option><option value="5">5</option>
                      </select>
                    </div>
                    <div>
                      <label className={labelClass}>Carrosserie</label>
                      <select name="body_type" value={manualForm.body_type} onChange={handleManualChange} className={inputClass}>
                        <option value="">Sélectionner</option>
                        {BODY_TYPES.map(bt => <option key={bt.value} value={bt.value}>{bt.label}</option>)}
                      </select>
                    </div>
                  </div>
                  <div>
                    <label className={labelClass}>Notes du vendeur</label>
                    <textarea name="description" value={manualForm.description} onChange={handleManualChange} rows={3} placeholder="Griffe, toit ouvrant, carnet complet..." className={inputClass} />
                    <p className="mt-1 text-xs text-muted">L'IA générera une description professionnelle à partir de vos notes.</p>
                  </div>
                </div>
              </div>

              <button type="submit" disabled={loading} className="w-full py-3 px-4 bg-carmin text-white rounded-lg hover:bg-carmin-light disabled:opacity-50 font-medium text-base">
                {loading ? (uploadProgress || 'Création...') : `Créer le véhicule${selectedFiles.length > 0 ? ` (${selectedFiles.length} photo${selectedFiles.length > 1 ? 's' : ''})` : ''}`}
              </button>
            </form>
          )}
        </div>
      )}

      {/* Manual tab */}
      {activeTab === 'manual' && (
        <form onSubmit={handleManualSubmit} className="space-y-6">
          {/* Photos section */}
          <div className="bg-dark-card rounded-lg p-6">
            <h2 className="text-lg font-semibold text-primary mb-4 flex items-center">
              <Image className="h-5 w-5 mr-2 text-secondary" />
              Photos
            </h2>

            {/* Photo previews */}
            {previewUrls.length > 0 && (
              <div className="grid grid-cols-3 sm:grid-cols-4 gap-3 mb-4">
                {previewUrls.map((url, index) => (
                  <div key={index} className="relative aspect-[4/3] rounded-lg overflow-hidden bg-dark-hover">
                    <img src={url} alt="" className="w-full h-full object-cover" />
                    <button
                      type="button"
                      onClick={() => removeFile(index)}
                      className="absolute top-1 right-1 bg-red-500 text-white rounded-full p-0.5 hover:bg-red-600"
                    >
                      <X className="h-3.5 w-3.5" />
                    </button>
                    {index === 0 && (
                      <span className="absolute bottom-1 left-1 bg-carmin text-white text-xs px-1.5 py-0.5 rounded">
                        Principale
                      </span>
                    )}
                  </div>
                ))}
              </div>
            )}

            <input
              type="file"
              ref={fileInputRef}
              onChange={handleFileSelect}
              multiple
              accept="image/*"
              className="hidden"
            />
            <button
              type="button"
              onClick={() => fileInputRef.current?.click()}
              className="w-full py-8 border-2 border-dashed border-dark-border rounded-lg text-muted hover:border-carmin hover:text-carmin transition-colors flex flex-col items-center"
            >
              <Upload className="h-8 w-8 mb-2" />
              <span className="text-sm font-medium">Cliquez pour ajouter des photos</span>
              <span className="text-xs text-secondary mt-1">JPG, PNG — plusieurs fichiers possibles</span>
            </button>
          </div>

          {/* Vehicle info */}
          <div className="bg-dark-card rounded-lg p-6">
            <h2 className="text-lg font-semibold text-primary mb-4">Informations du véhicule</h2>

            <div className="space-y-4">
              {/* Make + Model (with autocomplete) */}
              <div className="grid grid-cols-2 gap-4">
                <div className="relative" ref={makeRef}>
                  <label className={labelClass}>Marque *</label>
                  <input
                    type="text"
                    name="make"
                    value={manualForm.make}
                    onChange={(e) => {
                      handleManualChange(e);
                      handleMakeSearch(e.target.value);
                      setSelectedMakeKey(null);
                    }}
                    onFocus={() => makeSuggestions.length > 0 && setShowMakeSuggestions(true)}
                    placeholder="BMW, Audi, Mercedes..."
                    required
                    autoComplete="off"
                    className={inputClass}
                  />
                  {showMakeSuggestions && makeSuggestions.length > 0 && (
                    <div className="absolute z-50 w-full mt-1 bg-dark-card border border-dark-border rounded-lg shadow-lg max-h-48 overflow-y-auto">
                      {makeSuggestions.map(make => (
                        <button key={make.key} type="button" onClick={() => handleMakeSelect(make)} className="w-full text-left px-3 py-2 text-sm text-primary hover:bg-dark-hover">
                          {make.name}
                        </button>
                      ))}
                    </div>
                  )}
                </div>
                <div className="relative" ref={modelRef}>
                  <label className={labelClass}>Modèle *</label>
                  <input
                    type="text"
                    name="model"
                    value={manualForm.model}
                    onChange={(e) => {
                      handleManualChange(e);
                      if (modelSuggestions.length > 0) setShowModelSuggestions(true);
                    }}
                    onFocus={() => modelSuggestions.length > 0 && setShowModelSuggestions(true)}
                    placeholder="Série 3, A4, Classe C..."
                    required
                    autoComplete="off"
                    className={inputClass}
                  />
                  {showModelSuggestions && modelSuggestions.length > 0 && (
                    <div className="absolute z-50 w-full mt-1 bg-dark-card border border-dark-border rounded-lg shadow-lg max-h-48 overflow-y-auto">
                      {modelSuggestions
                        .filter(m => !manualForm.model || m.name.toLowerCase().includes(manualForm.model.toLowerCase()))
                        .map(model => (
                        <button key={model.key} type="button" onClick={() => handleModelSelect(model)} className="w-full text-left px-3 py-2 text-sm text-primary hover:bg-dark-hover">
                          {model.name}
                        </button>
                      ))}
                    </div>
                  )}
                </div>
              </div>

              {/* Version */}
              <div>
                <label className={labelClass}>Version / Finition</label>
                <input
                  type="text"
                  name="version"
                  value={manualForm.version}
                  onChange={handleManualChange}
                  placeholder="320d M Sport, 2.0 TDI S-Line..."
                  className={inputClass}
                />
              </div>

              {/* Year + Price */}
              <div className="grid grid-cols-2 gap-4">
                <div>
                  <label className={labelClass}>Année *</label>
                  <input
                    type="number"
                    name="year"
                    value={manualForm.year}
                    onChange={handleManualChange}
                    min="1900"
                    max="2100"
                    required
                    className={inputClass}
                  />
                </div>
                <div>
                  <label className={labelClass}>Prix (€) *</label>
                  <input
                    type="number"
                    name="price"
                    value={manualForm.price}
                    onChange={handleManualChange}
                    min="0"
                    placeholder="25000"
                    required
                    className={inputClass}
                  />
                </div>
              </div>

              {/* Purchase & reconditioning costs */}
              <div className="grid grid-cols-2 gap-4">
                <div>
                  <label className={labelClass}>Prix d'achat (€)</label>
                  <input
                    type="number"
                    name="purchase_price"
                    value={manualForm.purchase_price}
                    onChange={handleManualChange}
                    min="0"
                    placeholder="Optionnel"
                    className={inputClass}
                  />
                </div>
                <div>
                  <label className={labelClass}>Reconditionnement (€)</label>
                  <input
                    type="number"
                    name="reconditioning_cost"
                    value={manualForm.reconditioning_cost}
                    onChange={handleManualChange}
                    min="0"
                    placeholder="Optionnel"
                    className={inputClass}
                  />
                </div>
              </div>

              {/* Mileage + Power */}
              <div className="grid grid-cols-2 gap-4">
                <div>
                  <label className={labelClass}>Kilométrage</label>
                  <input
                    type="number"
                    name="mileage"
                    value={manualForm.mileage}
                    onChange={handleManualChange}
                    min="0"
                    placeholder="85000"
                    className={inputClass}
                  />
                </div>
                <div>
                  <label className={labelClass}>Puissance (kW)</label>
                  <input
                    type="number"
                    name="power_kw"
                    value={manualForm.power_kw}
                    onChange={handleManualChange}
                    min="0"
                    placeholder="140"
                    className={inputClass}
                  />
                </div>
              </div>

              {/* Fuel + Transmission */}
              <div className="grid grid-cols-2 gap-4">
                <div>
                  <label className={labelClass}>Carburant</label>
                  <select
                    name="fuel_type"
                    value={manualForm.fuel_type}
                    onChange={handleManualChange}
                    className={inputClass}
                  >
                    <option value="">Sélectionner</option>
                    {FUEL_TYPES.map(ft => (
                      <option key={ft.value} value={ft.value}>{ft.label}</option>
                    ))}
                  </select>
                </div>
                <div>
                  <label className={labelClass}>Transmission</label>
                  <select
                    name="transmission"
                    value={manualForm.transmission}
                    onChange={handleManualChange}
                    className={inputClass}
                  >
                    <option value="">Sélectionner</option>
                    {TRANSMISSION_OPTIONS.map(t => (
                      <option key={t.value} value={t.value}>{t.label}</option>
                    ))}
                  </select>
                </div>
              </div>

              {/* Color + Doors + Body type */}
              <div className="grid grid-cols-3 gap-4">
                <div>
                  <label className={labelClass}>Couleur</label>
                  {customColor ? (
                    <div className="flex gap-1">
                      <input
                        type="text"
                        name="color"
                        value={manualForm.color}
                        onChange={handleManualChange}
                        placeholder="Ex: Vert olive"
                        autoFocus
                        className={inputClass}
                      />
                      <button
                        type="button"
                        onClick={() => { setCustomColor(false); setManualForm(prev => ({ ...prev, color: '' })); }}
                        className="px-2 text-muted hover:text-primary shrink-0"
                        title="Retour à la liste"
                      >
                        <X className="h-4 w-4" />
                      </button>
                    </div>
                  ) : (
                    <select
                      name="color"
                      value={manualForm.color}
                      onChange={(e) => {
                        if (e.target.value === '__custom__') {
                          setCustomColor(true);
                          setManualForm(prev => ({ ...prev, color: '' }));
                        } else {
                          handleManualChange(e);
                        }
                      }}
                      className={inputClass}
                    >
                      <option value="">Sélectionner</option>
                      {COLORS.map(c => (
                        <option key={c.value} value={c.value}>{c.label}</option>
                      ))}
                      <option value="__custom__">Autre...</option>
                    </select>
                  )}
                </div>
                <div>
                  <label className={labelClass}>Portes</label>
                  <select
                    name="doors"
                    value={manualForm.doors}
                    onChange={handleManualChange}
                    className={inputClass}
                  >
                    <option value="">-</option>
                    <option value="2">2</option>
                    <option value="3">3</option>
                    <option value="4">4</option>
                    <option value="5">5</option>
                  </select>
                </div>
                <div>
                  <label className={labelClass}>Carrosserie</label>
                  <select
                    name="body_type"
                    value={manualForm.body_type}
                    onChange={handleManualChange}
                    className={inputClass}
                  >
                    <option value="">Sélectionner</option>
                    {BODY_TYPES.map(bt => (
                      <option key={bt.value} value={bt.value}>{bt.label}</option>
                    ))}
                  </select>
                </div>
              </div>

              {/* Seller Notes */}
              <div>
                <label className={labelClass}>Notes du vendeur</label>
                <textarea
                  name="description"
                  value={manualForm.description}
                  onChange={handleManualChange}
                  rows={3}
                  placeholder="Décrivez les spécificités en quelques mots : griffe à l'arrière, toit ouvrant, carnet d'entretien complet, garantie 12 mois..."
                  className={inputClass}
                />
                <p className="mt-1 text-xs text-muted">
                  L'IA générera une description professionnelle à partir de vos notes.
                </p>
              </div>
            </div>
          </div>

          {/* Extended specs */}
          <div className="bg-dark-card rounded-lg p-6">
            <h2 className="text-lg font-semibold text-primary mb-4">Spécifications techniques</h2>

            <div className="space-y-4">
              {/* Power HP + Seats */}
              <div className="grid grid-cols-2 gap-4">
                <div>
                  <label className={labelClass}>Puissance (ch)</label>
                  <input
                    type="number"
                    name="power_hp"
                    value={manualForm.power_hp}
                    onChange={handleManualChange}
                    min="0"
                    placeholder="190"
                    className={inputClass}
                  />
                </div>
                <div>
                  <label className={labelClass}>Places</label>
                  <select
                    name="seats"
                    value={manualForm.seats}
                    onChange={handleManualChange}
                    className={inputClass}
                  >
                    <option value="">-</option>
                    {[2,3,4,5,6,7,8,9].map(n => (
                      <option key={n} value={n}>{n}</option>
                    ))}
                  </select>
                </div>
              </div>

              {/* Cylinders + Displacement */}
              <div className="grid grid-cols-2 gap-4">
                <div>
                  <label className={labelClass}>Cylindres</label>
                  <select
                    name="cylinders"
                    value={manualForm.cylinders}
                    onChange={handleManualChange}
                    className={inputClass}
                  >
                    <option value="">-</option>
                    {[2,3,4,5,6,8,10,12].map(n => (
                      <option key={n} value={n}>{n}</option>
                    ))}
                  </select>
                </div>
                <div>
                  <label className={labelClass}>Cylindrée (cc)</label>
                  <input
                    type="number"
                    name="engine_displacement_cc"
                    value={manualForm.engine_displacement_cc}
                    onChange={handleManualChange}
                    min="0"
                    placeholder="1995"
                    className={inputClass}
                  />
                </div>
              </div>

              {/* Gears + Drive type */}
              <div className="grid grid-cols-2 gap-4">
                <div>
                  <label className={labelClass}>Vitesses</label>
                  <select
                    name="gears"
                    value={manualForm.gears}
                    onChange={handleManualChange}
                    className={inputClass}
                  >
                    <option value="">-</option>
                    {[4,5,6,7,8,9,10].map(n => (
                      <option key={n} value={n}>{n}</option>
                    ))}
                  </select>
                </div>
                <div>
                  <label className={labelClass}>Transmission</label>
                  <select
                    name="drive_type"
                    value={manualForm.drive_type}
                    onChange={handleManualChange}
                    className={inputClass}
                  >
                    <option value="">Sélectionner</option>
                    {DRIVE_TYPES.map(dt => (
                      <option key={dt.value} value={dt.value}>{dt.label}</option>
                    ))}
                  </select>
                </div>
              </div>

              {/* Fuel consumption + CO2 */}
              <div className="grid grid-cols-2 gap-4">
                <div>
                  <label className={labelClass}>Consommation (l/100km)</label>
                  <input
                    type="number"
                    name="fuel_consumption"
                    value={manualForm.fuel_consumption}
                    onChange={handleManualChange}
                    min="0"
                    step="0.1"
                    placeholder="5.4"
                    className={inputClass}
                  />
                </div>
                <div>
                  <label className={labelClass}>CO₂ (g/km)</label>
                  <input
                    type="number"
                    name="co2_emissions"
                    value={manualForm.co2_emissions}
                    onChange={handleManualChange}
                    min="0"
                    placeholder="142"
                    className={inputClass}
                  />
                </div>
              </div>

              {/* Euro standard + Weight */}
              <div className="grid grid-cols-2 gap-4">
                <div>
                  <label className={labelClass}>Norme Euro</label>
                  <select
                    name="euro_standard"
                    value={manualForm.euro_standard}
                    onChange={handleManualChange}
                    className={inputClass}
                  >
                    <option value="">Sélectionner</option>
                    {EURO_STANDARDS.map(es => (
                      <option key={es.value} value={es.value}>{es.label}</option>
                    ))}
                  </select>
                </div>
                <div>
                  <label className={labelClass}>Poids</label>
                  <input
                    type="text"
                    name="weight"
                    value={manualForm.weight}
                    onChange={handleManualChange}
                    placeholder="1450 kg"
                    className={inputClass}
                  />
                </div>
              </div>

              {/* Interior type + Interior color + Paint */}
              <div className="grid grid-cols-3 gap-4">
                <div>
                  <label className={labelClass}>Intérieur</label>
                  <select
                    name="interior_type"
                    value={manualForm.interior_type}
                    onChange={handleManualChange}
                    className={inputClass}
                  >
                    <option value="">Sélectionner</option>
                    {INTERIOR_TYPES.map(it => (
                      <option key={it.value} value={it.value}>{it.label}</option>
                    ))}
                  </select>
                </div>
                <div>
                  <label className={labelClass}>Couleur intérieure</label>
                  <input
                    type="text"
                    name="interior_color"
                    value={manualForm.interior_color}
                    onChange={handleManualChange}
                    placeholder="Noir, Beige..."
                    className={inputClass}
                  />
                </div>
                <div>
                  <label className={labelClass}>Type de peinture</label>
                  <select
                    name="paint_type"
                    value={manualForm.paint_type}
                    onChange={handleManualChange}
                    className={inputClass}
                  >
                    <option value="">Sélectionner</option>
                    {PAINT_TYPES.map(pt => (
                      <option key={pt.value} value={pt.value}>{pt.label}</option>
                    ))}
                  </select>
                </div>
              </div>
            </div>
          </div>

          {/* History & condition */}
          <div className="bg-dark-card rounded-lg p-6">
            <h2 className="text-lg font-semibold text-primary mb-4">Historique & état</h2>

            <div className="space-y-4">
              {/* First registration + Previous owners */}
              <div className="grid grid-cols-2 gap-4">
                <div>
                  <label className={labelClass}>1ère immatriculation</label>
                  <input
                    type="text"
                    name="first_registration"
                    value={manualForm.first_registration}
                    onChange={handleManualChange}
                    placeholder="03/2020"
                    className={inputClass}
                  />
                </div>
                <div>
                  <label className={labelClass}>Propriétaires précédents</label>
                  <input
                    type="number"
                    name="previous_owners"
                    value={manualForm.previous_owners}
                    onChange={handleManualChange}
                    min="0"
                    placeholder="1"
                    className={inputClass}
                  />
                </div>
              </div>

              {/* Warranty */}
              <div>
                <label className={labelClass}>Garantie</label>
                <input
                  type="text"
                  name="warranty"
                  value={manualForm.warranty}
                  onChange={handleManualChange}
                  placeholder="12 mois, constructeur 2027..."
                  className={inputClass}
                />
              </div>

              {/* Boolean toggles */}
              <div className="grid grid-cols-2 gap-4">
                <div>
                  <label className={labelClass}>Accidenté</label>
                  <select
                    name="had_accident"
                    value={manualForm.had_accident}
                    onChange={handleManualChange}
                    className={inputClass}
                  >
                    <option value="">Non renseigné</option>
                    <option value="false">Non</option>
                    <option value="true">Oui</option>
                  </select>
                </div>
                <div>
                  <label className={labelClass}>Carnet d'entretien complet</label>
                  <select
                    name="has_full_service_history"
                    value={manualForm.has_full_service_history}
                    onChange={handleManualChange}
                    className={inputClass}
                  >
                    <option value="">Non renseigné</option>
                    <option value="true">Oui</option>
                    <option value="false">Non</option>
                  </select>
                </div>
              </div>

              <div className="grid grid-cols-2 gap-4">
                <div>
                  <label className={labelClass}>Non-fumeur</label>
                  <select
                    name="non_smoking"
                    value={manualForm.non_smoking}
                    onChange={handleManualChange}
                    className={inputClass}
                  >
                    <option value="">Non renseigné</option>
                    <option value="true">Oui</option>
                    <option value="false">Non</option>
                  </select>
                </div>
                <div>
                  <label className={labelClass}>Filtre à particules</label>
                  <select
                    name="has_particle_filter"
                    value={manualForm.has_particle_filter}
                    onChange={handleManualChange}
                    className={inputClass}
                  >
                    <option value="">Non renseigné</option>
                    <option value="true">Oui</option>
                    <option value="false">Non</option>
                  </select>
                </div>
              </div>

              <div>
                <label className={labelClass}>Déductible TVA</label>
                <select
                  name="tax_deductible"
                  value={manualForm.tax_deductible}
                  onChange={handleManualChange}
                  className={inputClass}
                >
                  <option value="">Non renseigné</option>
                  <option value="true">Oui</option>
                  <option value="false">Non</option>
                </select>
              </div>
            </div>
          </div>

          {/* Submit */}
          <button
            type="submit"
            disabled={loading}
            className="w-full py-3 px-4 bg-carmin text-white rounded-lg hover:bg-carmin-light disabled:opacity-50 font-medium text-base"
          >
            {loading ? (uploadProgress || 'Création...') : `Créer le véhicule${selectedFiles.length > 0 ? ` (${selectedFiles.length} photo${selectedFiles.length > 1 ? 's' : ''})` : ''}`}
          </button>
        </form>
      )}
    </div>
  );
};

export default VehicleAdd;
